/**
 * The skirmish AI (U13).
 *
 * **A client of the command API on equal footing with the human.** Same
 * commands, same fog of war, no privileged state access. That is not politeness
 * — it is what makes a bot match a real test of the game rather than a test of
 * a parallel implementation, and it is what keeps R19 honest.
 *
 * A small prioritised behaviour set, evaluated on a slow tick:
 *   keep harvesters saturated → maintain a build order toward a target army
 *   composition → defend when the base takes damage → attack when army value
 *   exceeds a threshold → expand when nearby fields deplete.
 */

import type { Ctx, Subsystem } from '../core/registry.js'
import * as fix from '../core/fixed.js'
import { MAP_SIZE, MATCH_TICK_CAP, MAX_PLAYERS, MAX_QUEUE_DEPTH, TICK_HZ } from '../core/config.js'
import { NO_WEAPON } from './data/weapons.js'
import { World } from './world.js'
import { Entities, EF, ST, NO_ENTITY } from './entities.js'
import { Vision } from './vision.js'
import { Economy } from './economy.js'
import { Build } from './build.js'
import type { Relay } from '../net/relay.js'
import { CMD, type Command } from '../net/commands.js'
import {
  ROLE,
  TAB,
  defById,
  defsForFaction,
  founderUnit,
  rootStructure,
  type EntityDef,
} from './data/entities.js'

/** Ticks between AI decisions. Ten per second is far more than a human. */
const THINK_INTERVAL = 10

export const DIFFICULTY = { Easy: 0, Normal: 1, Hard: 2, Passive: 3 } as const

interface Handicap {
  /** Ticks before this AI does anything at all. */
  readonly graceTicks: number
  /** Multiplier on the interval between decisions. */
  readonly thinkEvery: number
  /** Multiplier on the army value it gathers before committing to an attack. */
  readonly attackScale: number
  /** Ceiling on combat units it will keep in the field. */
  readonly armyCap: number
}

/**
 * Three ways to be easier, none of which make the AI play badly.
 *
 * The complaint that produced this was "it gets so many units so quickly before
 * I even get a chance", and that is exactly right: the AI does not cheat — same
 * starting credits, same build times, same prices — it simply never idles. A
 * human spends the first two minutes reading the sidebar; the AI spends them
 * executing a build order at machine speed.
 *
 * So the knobs are time and volume, not competence. Easy starts a minute late,
 * thinks a third as often, keeps a small army and needs a much bigger one
 * before it attacks. Hard is the behaviour that existed before this setting.
 *
 * **Every level below Hard was moved down again after play reports.** Normal is the
 * default — it is what `?ai=` falls back to and what a skirmish seats — and several
 * people independently described it as near-perfect play. They were not wrong: at the
 * old figures Normal still took a decision every twenty ticks, roughly one and a half
 * per second sustained, against a human who pauses to read a sidebar and does not
 * open with the right building. Normal is now close to what Easy was, and Easy is
 * genuinely gentle. Hard is untouched and remains the reference: no grace, no
 * throttle, no cap.
 */
const HANDICAPS: readonly Handicap[] = [
  { graceTicks: 90 * TICK_HZ, thinkEvery: 5, attackScale: 2.4, armyCap: 6 },
  { graceTicks: 50 * TICK_HZ, thinkEvery: 3, attackScale: 1.7, armyCap: 12 },
  { graceTicks: 0, thinkEvery: 1, attackScale: 1, armyCap: 999 },
  // Passive: a grace period that never ends. The AI never deploys, builds, or
  // attacks — a testing target, reached via `?ai=passive`, not a difficulty a
  // player would pick.
  { graceTicks: Infinity, thinkEvery: 1, attackScale: 1, armyCap: 0 },
]
/** Army value at which the AI commits to an attack. */
const ATTACK_THRESHOLD = 4500
/**
 * Army value below which the AI will not press for a kill, whatever the enemy
 * looks like. Roughly two units: fewer than that is a donation, not a finish.
 */
const FINISH_FLOOR = 700

/** Aircraft one player will keep. See `buildArmy`. */
const MAX_AIRCRAFT = 4

/** Cells from the base centre within which an enemy unit counts as an attack. */
const DEFEND_RADIUS = 22

/** Main-battle units the AI wants standing in front of each artillery piece. */
const ARTILLERY_ESCORT = 3

/** Harvesters the AI wants per refinery. */
const HARVESTERS_PER_REFINERY = 3
/** Below this per refinery, the AI buys nothing but harvesters. */
const MIN_HARVESTERS_PER_REFINERY = 2
/** Credits kept in reserve so the build order never starves. */
const RESERVE = 300

interface Personality {
  /** Preferred build order by role, after the economy opening. */
  readonly techPriority: readonly string[]
  /** How much army value to accumulate before attacking. */
  readonly attackThreshold: number
}

/**
 * Temperaments, one per slot.
 *
 * These exist to break a tie that nothing else can. A mirror matchup — same
 * faction on both sides — is played on a map that is rotationally symmetric by
 * construction, by two copies of the same AI running the same build order
 * against each other. There is no asymmetry for either side to convert into a
 * win, and the sweep says so: mirror matchups stalled at 20% against 7% for
 * non-mirror ones, three times the rate, on the same maps and the same code.
 *
 * Distinct openings give each side something the other does not have at some
 * point in the match, and a moment where one is ahead is a moment one of them
 * can win from. Assigned by slot index, so two players never share one.
 *
 * Superweapon before airbase in every order: it is the faster answer to a
 * stalemate, and ordering it second measurably delayed the thing that ends long
 * matches.
 */
const PERSONALITIES: Personality[] = [
  {
    // Straight to armour. Fewest buildings before the first real attack.
    techPriority: [
      ROLE.Power,
      ROLE.Refinery,
      ROLE.VehicleProduction,
      ROLE.Power,
      ROLE.InfantryProduction,
      ROLE.TechLab,
      ROLE.Power,
      ROLE.Superweapon,
      ROLE.Defense,
      ROLE.AdvancedLab,
      ROLE.Airbase,
    ],
    attackThreshold: ATTACK_THRESHOLD * 0.7,
  },
  {
    // Two refineries before an army, and a bigger one when it comes.
    techPriority: [
      ROLE.Power,
      ROLE.Refinery,
      ROLE.Refinery,
      ROLE.VehicleProduction,
      ROLE.Power,
      ROLE.Defense,
      ROLE.InfantryProduction,
      ROLE.TechLab,
      ROLE.Power,
      ROLE.AdvancedLab,
      ROLE.Superweapon,
      ROLE.Airbase,
    ],
    attackThreshold: ATTACK_THRESHOLD * 1.3,
  },
  {
    // Turtle and tech: defences early, superweapon as the win condition.
    techPriority: [
      ROLE.Power,
      ROLE.Refinery,
      ROLE.Defense,
      ROLE.VehicleProduction,
      ROLE.Power,
      ROLE.TechLab,
      ROLE.AdvancedLab,
      ROLE.Superweapon,
      ROLE.InfantryProduction,
      ROLE.Power,
      ROLE.Airbase,
    ],
    attackThreshold: ATTACK_THRESHOLD * 1.1,
  },
  {
    // Infantry first, then armour. Cheapest early pressure of the four.
    techPriority: [
      ROLE.Power,
      ROLE.Refinery,
      ROLE.InfantryProduction,
      ROLE.VehicleProduction,
      ROLE.Power,
      ROLE.TechLab,
      ROLE.Superweapon,
      ROLE.Power,
      ROLE.Defense,
      ROLE.Airbase,
      ROLE.AdvancedLab,
    ],
    attackThreshold: ATTACK_THRESHOLD * 0.85,
  },
]

export class SkirmishAI implements Subsystem {
  static id = 'ai'
  static deps = ['world', 'entities', 'pathfind', 'vision', 'economy', 'build'] as const

  private ctx!: Ctx
  private world!: World
  private entities!: Entities
  private vision!: Vision
  private economy!: Economy
  private build!: Build

  /** Slots the AI plays. Set by the composition root or the bot-match harness. */
  readonly controlled = new Set<number>()
  relay: Relay | null = null

  private personalityFor(player: number): Personality {
    return PERSONALITIES[player % PERSONALITIES.length]
  }
  /** How far through the tech priority each player is. */
  readonly techStep = new Int32Array(MAX_PLAYERS)
  /** Tick each player last committed to an attack. */
  readonly lastAttack = new Int32Array(MAX_PLAYERS).fill(-10_000)
  private readonly baseUnderAttack = new Int32Array(MAX_PLAYERS).fill(-10_000)
  /** 1 while a player is committed to an attack wave. */
  readonly attacking = new Uint8Array(MAX_PLAYERS)
  /** The cell the army gathers at between waves. */
  private readonly rally = new Int32Array(MAX_PLAYERS).fill(-1)
  /**
   * Per-slot aggression, in hundredths, scaling the attack threshold.
   *
   * Two identical AIs on a rotationally symmetric map produce a perfectly
   * symmetric attrition equilibrium and the match never resolves — the sweep
   * showed every mirror pairing running to the tick cap. Deterministic
   * per-slot variation breaks the symmetry without making either side weaker,
   * and it reads as two commanders with different temperaments rather than as
   * a coin flip.
   */
  readonly aggression = new Int32Array(MAX_PLAYERS).fill(100)
  /** Tick each player last dispatched a scout. */
  private readonly lastScout = new Int32Array(MAX_PLAYERS).fill(-10_000)

  init(ctx: Ctx): void {
    this.ctx = ctx
    this.world = ctx.get<World>('world')
    this.entities = ctx.get<Entities>('entities')
    this.vision = ctx.get<Vision>('vision')
    this.economy = ctx.get<Economy>('economy')
    this.build = ctx.get<Build>('build')

    // Tech progress is simulation state — two clients running the same AI must
    // reach the same decisions — so it is hashed like everything else.
    ctx.hash.register('ai:techStep', this.techStep)
    ctx.hash.register('ai:lastAttack', this.lastAttack)
    ctx.hash.register('ai:attacking', this.attacking)
    ctx.hash.register('ai:rally', this.rally)
    ctx.hash.register('ai:aggression', this.aggression)
    ctx.hash.register('ai:searchStep', this.searchStep)
    ctx.hash.register('ai:lastScout', this.lastScout)

    // Drawn from the simulation RNG, so every client derives the same
    // temperaments from the same match definition.
    for (let slot = 0; slot < MAX_PLAYERS; slot++) {
      this.aggression[slot] = 70 + ctx.rng.int(61)
    }

    ctx.on('actor:damaged', (p) => this.onDamaged(p as { id: number }))
  }

  control(slot: number, difficulty: number = DIFFICULTY.Normal): void {
    this.controlled.add(slot)
    this.difficulty[slot] = Math.max(0, Math.min(HANDICAPS.length - 1, difficulty))
  }

  /** Per-slot difficulty. Hashed, because it changes what the AI does. */
  private readonly difficulty = new Int32Array(MAX_PLAYERS).fill(DIFFICULTY.Normal)

  private handicap(player: number): Handicap {
    return HANDICAPS[this.difficulty[player]] ?? HANDICAPS[DIFFICULTY.Normal]
  }

  private onDamaged(p: { id: number }): void {
    const slot = this.entities.resolve(p.id)
    if (slot < 0) return
    if (!this.entities.has(slot, EF.Structure)) return
    const owner = this.entities.owner[slot]
    if (owner >= 0) this.baseUnderAttack[owner] = this.ctx.time.tick
  }

  fixedUpdate(tick: number): void {
    if (!this.relay) return
    if (tick % THINK_INTERVAL !== 0) return
    // Ascending slot order, always. Iterating the Set directly would make AI
    // decision order depend on insertion order, which differs between clients.
    for (let slot = 0; slot < MAX_PLAYERS; slot++) {
      if (!this.controlled.has(slot)) continue
      const handicap = this.handicap(slot)
      // An opening grace period, and a slower decision cadence.
      //
      // Handicapping the AI by making it play *badly* — worse targeting, worse
      // build orders — produces an opponent that is easy and also boring to
      // beat. Slowing it down instead leaves every decision it makes a good
      // one; there are simply fewer of them, and they start later. It plays the
      // same game, just less of it.
      if (tick < handicap.graceTicks) continue
      if (tick % (THINK_INTERVAL * handicap.thinkEvery) !== 0) continue
      this.think(slot, tick)
    }
  }

  private think(player: number, tick: number): void {
    const faction = this.factionOf(player)
    if (faction < 0) return

    this.openWithDeploy(player, faction)
    this.placePending(player)

    // Harvesters come first, and when the economy is below its floor nothing
    // else is bought at all.
    //
    // Without that gate the AI can spend itself into a hole it cannot climb
    // out of: a sweep seed produced a player with a complete seven-building
    // base, one harvester, and 336 credits — too poor to afford the harvester
    // that would fix its income, and therefore permanently unable to build an
    // army. The match ran to the tick cap with one side simply frozen.
    const starved = this.economyBelowFloor(player)
    this.keepHarvestersSaturated(player, faction, starved)
    this.keepScouting(player, tick)
    if (!starved) {
      this.keepPoweredUp(player, faction)
      this.rebuildEssentials(player, faction)
      this.advanceTech(player, faction)
      this.buildArmy(player, faction)
    }
    this.fireSuperweapon(player)
    this.commandArmy(player, faction, tick)
  }

  private factionOf(player: number): number {
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s) || this.entities.owner[s] !== player) continue
      const def = defById(this.entities.kind[s])
      if (def) return def.faction
    }
    return -1
  }

  // --- opening -------------------------------------------------------------

  /** F1's first move: deploy the founder into a base. */
  private openWithDeploy(player: number, faction: number): void {
    if (this.build.hasStructure(player, rootStructure(faction).id)) return
    const founder = founderUnit(faction)
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s) || this.entities.owner[s] !== player) continue
      if (this.entities.kind[s] !== founder.id) continue
      this.submit(player, { t: CMD.Deploy, unit: this.entities.idAt(s) })
      return
    }
  }

  /**
   * Place anything awaiting a footprint. Searches outward from the base centre
   * for a legal spot, which is what makes the build-area rule survive contact
   * with an AI that cannot see a ghost cursor.
   */
  private placePending(player: number): void {
    const count = this.build.readyCount(player)
    if (count === 0) return
    const kind = this.build.readyKindAt(player, 0)
    const def = defById(kind)
    if (!def) return

    const centre = this.baseCentre(player)
    if (centre < 0) return
    const bx = centre % MAP_SIZE
    const by = (centre / MAP_SIZE) | 0

    for (let r = 1; r < 14; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (dx > -r && dx < r && dy > -r && dy < r) continue
          const cx = bx + dx
          const cy = by + dy
          if (this.build.footprintFailure(player, def, cx, cy, true) !== null) continue
          if (this.wouldSealARefinery(def.width, def.height, cx, cy)) continue
          this.submit(player, { t: CMD.Place, kind, cx, cy })
          return
        }
      }
    }
  }

  /**
   * Would this footprint seal a refinery away from its own harvesters?
   *
   * Placement only requires being inside the build area, so a greedy search
   * outward from the base centre will pack buildings into a solid block — and a
   * sweep seed did exactly that, walling a refinery in so completely that its
   * six harvesters spent the entire match in `Returning` with fifty-eight
   * thousand units of explored ore in front of them.
   *
   * The check is deliberately narrow. A first attempt demanded a third of every
   * footprint's perimeter stay open and was far too strict: the AI could barely
   * place anything, and stalls went from two in a hundred and sixty to twenty.
   * What actually has to hold is that the buildings harvesters must reach keep
   * a way in.
   */
  private wouldSealARefinery(w: number, h: number, cx: number, cy: number): boolean {
    const blocked = (x: number, y: number): boolean =>
      x >= cx && x < cx + w && y >= cy && y < cy + h

    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      if (!this.entities.has(s, EF.Structure)) continue
      const def = defById(this.entities.kind[s])
      if (def?.role !== ROLE.Refinery) continue

      const cell = this.world.cellAt(fix.raw(this.entities.x[s]), fix.raw(this.entities.y[s]))
      if (cell < 0) continue
      const rx = (cell % MAP_SIZE) - (def.width >> 1)
      const ry = ((cell / MAP_SIZE) | 0) - (def.height >> 1)

      let open = 0
      for (let x = rx - 1; x <= rx + def.width; x++) {
        for (const y of [ry - 1, ry + def.height]) {
          if (!blocked(x, y) && this.world.isPassable(x, y)) open++
        }
      }
      for (let y = ry; y < ry + def.height; y++) {
        for (const x of [rx - 1, rx + def.width]) {
          if (!blocked(x, y) && this.world.isPassable(x, y)) open++
        }
      }
      if (open < 3) return true
    }
    return false
  }

  private baseCentre(player: number): number {
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s) || this.entities.owner[s] !== player) continue
      if (!this.entities.has(s, EF.Structure)) continue
      const def = defById(this.entities.kind[s])
      if (def?.role !== ROLE.BaseRoot) continue
      return this.world.cellAt(fix.raw(this.entities.x[s]), fix.raw(this.entities.y[s]))
    }
    // No root yet: fall back to any owned entity.
    for (let s = 0; s < limit; s++) {
      if (this.entities.isAlive(s) && this.entities.owner[s] === player) {
        return this.world.cellAt(fix.raw(this.entities.x[s]), fix.raw(this.entities.y[s]))
      }
    }
    return -1
  }

  // --- economy -------------------------------------------------------------

  /**
   * Fewer harvesters than the minimum a refinery needs to stay busy — **and**
   * a harvester is actually buildable right now.
   *
   * The second half matters: the free harvester arrives with the refinery, long
   * before the vehicle factory that could build a second one. Treating that as
   * starvation blocks the tech order that would produce the factory, and the AI
   * deadlocks at four buildings for the whole match.
   */
  private economyBelowFloor(player: number): boolean {
    const refineries = this.countRole(player, ROLE.Refinery)
    if (refineries === 0) return false
    if (this.countHarvesters(player) >= refineries * MIN_HARVESTERS_PER_REFINERY) return false
    const faction = this.factionOf(player)
    const harvester = defsForFaction(faction).find((d) => d.role === ROLE.Harvester)
    return !!harvester && this.build.canBuild(player, harvester.id)
  }

  private keepHarvestersSaturated(player: number, faction: number, starved: boolean): void {
    const refineries = this.countRole(player, ROLE.Refinery)
    if (refineries === 0) return
    const harvesters = this.countHarvesters(player)
    if (harvesters >= refineries * HARVESTERS_PER_REFINERY) return
    // Buying a fourth harvester while two are idle is burning credits on the
    // symptom rather than the cause.
    if (this.idleHarvesters(player) > 0) return

    const harvester = defsForFaction(faction).find((d) => d.role === ROLE.Harvester)
    if (!harvester) return
    if (!this.build.canBuild(player, harvester.id)) return
    // A harvester pays for itself, so below the floor it is bought with the
    // last credit rather than held back behind a reserve.
    const needed = starved ? harvester.cost : harvester.cost + RESERVE
    if (this.economy.creditsOf(player) < needed) return
    if (this.build.queueDepth(player, harvester.tab) > 1) return
    this.submit(player, { t: CMD.Queue, tab: harvester.tab, kind: harvester.id })
  }

  /**
   * Build power reactively, not only where it appears in the tech order.
   *
   * The tech list walks forward and never revisits, so a base that outgrows its
   * grid later — as it does the moment defences and a tech lab land — sits in a
   * permanent deficit, building everything at a fraction of rate and never
   * charging its superweapon. Watching headroom fixes that without making the
   * build order adaptive.
   */
  private keepPoweredUp(player: number, faction: number): void {
    if (this.powerHeadroom(player) >= 30) return
    const power = this.cheapestForRole(faction, ROLE.Power)
    if (!power || !this.build.canBuild(player, power.id)) return
    if (this.build.queueDepth(player, TAB.Structure) > 0) return
    if (this.build.readyCount(player) > 0) return
    if (this.economy.creditsOf(player) < power.cost) return
    this.submit(player, { t: CMD.Queue, tab: power.tab, kind: power.id })
  }

  /**
   * Replace a destroyed refinery or production building.
   *
   * The tech order walks forward and never revisits, so without this an AI that
   * loses its refinery to a raid simply stops mining for the rest of the match —
   * which showed up in the sweep as harvesters idling for ninety seconds with a
   * full map of regolith in front of them.
   */
  private rebuildEssentials(player: number, faction: number): void {
    if (this.build.queueDepth(player, TAB.Structure) > 0) return
    if (this.build.readyCount(player) > 0) return

    for (const role of [ROLE.Refinery, ROLE.VehicleProduction, ROLE.InfantryProduction]) {
      if (this.countRole(player, role) > 0) continue
      const def = this.cheapestForRole(faction, role)
      if (!def || !this.build.canBuild(player, def.id)) continue
      if (this.economy.creditsOf(player) < def.cost) continue
      this.submit(player, { t: CMD.Queue, tab: def.tab, kind: def.id })
      return
    }
  }

  /**
   * Send a unit to reveal unexplored ground when the economy runs out of
   * *known* regolith.
   *
   * Harvesters may only target fields in explored territory (F2), which is the
   * correct rule — but an AI that never scouts eventually depletes everything
   * it has seen and then idles six harvesters in front of half a million units
   * of ore it has simply never looked at. That was the shape of every remaining
   * stall in the sweep: two live bases, no armies, and a full map.
   */
  private keepScouting(player: number, tick: number): void {
    if (tick - this.lastScout[player] < 12 * TICK_HZ) return
    // Only bother when the economy actually needs it.
    if (this.idleHarvesters(player) === 0) return

    const target = this.nearestUnexplored(player)
    if (target < 0) return

    // Prefer a dedicated scout; fall back to any spare combat unit, because an
    // idle economy is worth more than one more rifle in the rally point.
    const scout = this.pickScout(player)
    if (scout === NO_ENTITY) return

    this.lastScout[player] = tick
    this.submit(player, {
      t: CMD.Move,
      units: Uint32Array.from([scout]),
      x: this.world.cellCenterX(target % MAP_SIZE),
      y: this.world.cellCenterY((target / MAP_SIZE) | 0),
    })
  }

  /**
   * A unit to send into the dark: a dedicated scout, else any spare combat
   * unit, else **an idle harvester**.
   *
   * That last fallback breaks a doom loop the sweep exposed. A player whose
   * known fields run dry stops earning; with no income it cannot build an army;
   * with no army it has nothing to scout with; having never scouted it never
   * finds the half a million units of ore still on the map. It then sits at
   * eleven buildings and one tank for the rest of the match. A harvester
   * driving into the dark when it has nothing else to do is exactly what a
   * human does, and it is the only unit that is *already* idle.
   */
  private pickScout(player: number): number {
    let combat = NO_ENTITY
    let harvester = NO_ENTITY
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s) || this.entities.owner[s] !== player) continue
      if (!this.entities.has(s, EF.Mobile)) continue
      if (this.entities.has(s, EF.Harvester)) {
        if (harvester === NO_ENTITY && this.entities.state[s] === ST.Idle) {
          harvester = this.entities.idAt(s)
        }
        continue
      }
      const def = defById(this.entities.kind[s])
      if (!def || def.role === ROLE.Founder) continue
      if (def.role === ROLE.Scout) return this.entities.idAt(s)
      if (combat === NO_ENTITY) combat = this.entities.idAt(s)
    }
    return combat !== NO_ENTITY ? combat : harvester
  }

  /** Nearest unexplored cell that still holds regolith worth walking to. */
  private nearestUnexplored(player: number): number {
    const base = this.baseCentre(player)
    if (base < 0) return -1
    const bx = base % MAP_SIZE
    const by = (base / MAP_SIZE) | 0

    let best = -1
    let bestD = Infinity
    // Sampled on a coarse lattice: this runs on the think tick, and a full
    // 16,384-cell scan for a scouting hint is effort spent in the wrong place.
    for (let cy = 2; cy < MAP_SIZE - 2; cy += 3) {
      for (let cx = 2; cx < MAP_SIZE - 2; cx += 3) {
        const cell = cy * MAP_SIZE + cx
        if (this.vision.isExplored(player, cell)) continue
        if (this.world.regolith[cell] <= 0) continue
        const d = (cx - bx) * (cx - bx) + (cy - by) * (cy - by)
        if (d < bestD) {
          bestD = d
          best = cell
        }
      }
    }
    return best
  }

  // --- tech ----------------------------------------------------------------

  private advanceTech(player: number, faction: number): void {
    const step = this.techStep[player]
    const order = this.personalityFor(player).techPriority
    if (step >= order.length) return
    const role = order[step]

    const target = this.cheapestForRole(faction, role)
    if (!target) {
      this.techStep[player]++
      return
    }

    // Power is special: it is re-entered several times in the priority list, so
    // "already have one" must not skip the step permanently.
    if (role !== ROLE.Power && this.countRole(player, role) > 0) {
      this.techStep[player]++
      return
    }
    if (role === ROLE.Power && this.powerHeadroom(player) > 40) {
      this.techStep[player]++
      return
    }

    if (!this.build.canBuild(player, target.id)) return
    if (this.build.queueDepth(player, TAB.Structure) > 0) return
    if (this.build.readyCount(player) > 0) return
    if (this.economy.creditsOf(player) < target.cost) return

    if (this.submit(player, { t: CMD.Queue, tab: target.tab, kind: target.id })) {
      this.techStep[player]++
    }
  }

  private powerHeadroom(player: number): number {
    return this.economy.powerProduced[player] - this.economy.powerConsumed[player]
  }

  private cheapestForRole(faction: number, role: string): EntityDef | undefined {
    let best: EntityDef | undefined
    for (const def of defsForFaction(faction)) {
      if (def.role !== role) continue
      if (!best || def.cost < best.cost) best = def
    }
    return best
  }

  /**
   * Fire the superweapon at the densest visible cluster of enemy structures.
   *
   * Without this the AI charges a superweapon for four minutes and never uses
   * it, which is both a wasted 5000 credits and — more importantly — the loss
   * of the one mechanic designed to break a stalemate. The sweep showed six
   * matches in forty running to the tick cap as pure attrition; a weapon that
   * removes a base cluster is what a stalemate is supposed to end on.
   */
  private fireSuperweapon(player: number): void {
    if (!this.build.isSuperweaponReady(player)) return

    let bestCell = -1
    let bestScore = 0
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      const owner = this.entities.owner[s]
      if (owner < 0 || owner === player) continue
      if (!this.entities.has(s, EF.Structure)) continue
      if (!this.vision.canSee(player, s)) continue

      const cell = this.world.cellAt(fix.raw(this.entities.x[s]), fix.raw(this.entities.y[s]))
      if (cell < 0) continue
      const cx = cell % MAP_SIZE
      const cy = (cell / MAP_SIZE) | 0

      // Score by how many other enemy entities sit inside the blast radius, so
      // the shot lands on a base rather than on an outlying turret.
      let score = 0
      for (let t = 0; t < limit; t++) {
        if (!this.entities.isAlive(t)) continue
        const tOwner = this.entities.owner[t]
        if (tOwner < 0 || tOwner === player) continue
        const tx = this.entities.x[t] >> 16
        const ty = this.entities.y[t] >> 16
        const d2 = (tx - cx) * (tx - cx) + (ty - cy) * (ty - cy)
        if (d2 <= 25) score += this.entities.has(t, EF.Structure) ? 3 : 1
      }
      if (score > bestScore) {
        bestScore = score
        bestCell = cell
      }
    }

    // Nothing currently visible. Fall back to the last-known position of an
    // enemy structure, which is exactly the knowledge a human commander aims a
    // superweapon with — and without this a turtling AI charges one for four
    // minutes and never fires it, because it has no scout near the enemy base.
    if (bestCell < 0) bestCell = this.rememberedEnemyStructure(player)
    if (bestCell < 0) return

    this.submit(player, {
      t: CMD.SuperFire,
      kind: this.build.superKind[player],
      cx: bestCell % MAP_SIZE,
      cy: (bestCell / MAP_SIZE) | 0,
    })
  }

  /** Search cursor per player, advanced when the enemy cannot be found. */
  private readonly searchStep = new Int32Array(MAX_PLAYERS)

  /** A cell where this player last saw an enemy structure, or -1. */
  private rememberedEnemyStructure(player: number): number {
    for (let cell = 0; cell < this.world.cells; cell++) {
      if (this.vision.rememberedAt(player, cell) !== 0) return cell
    }
    // Never seen one at all: the enemy start marker is public knowledge.
    for (let p = 0; p < MAX_PLAYERS; p++) {
      if (p === player) continue
      const sx = this.world.starts[p * 2]
      const sy = this.world.starts[p * 2 + 1]
      if (sx >= 0) return sy * MAP_SIZE + sx
    }
    return -1
  }

  // --- army ----------------------------------------------------------------

  /**
   * Turn income into army.
   *
   * The queue depth scales with the credit balance rather than sitting at a
   * fixed four. A sweep seed produced an AI holding **27,828 credits** with
   * nineteen units in the field: it was producing exactly as fast as it was
   * losing units and could not spend the difference, so the match ran to the
   * tick cap with a bank account instead of an army. A human in that position
   * queues twenty tanks.
   */
  private buildArmy(player: number, faction: number): void {
    const credits = this.economy.creditsOf(player)
    if (credits < RESERVE) return
    // An easier AI keeps a smaller field army. It still builds, still teches
    // and still defends — there is simply less of it to meet.
    if (this.combatUnitCount(player) >= this.handicap(player).armyCap) return

    // A simple composition: mostly main battle units, some anti-infantry, and
    // aircraft once the airbase is up. Air is listed last so it is bought with
    // surplus rather than instead of an army — a fleet of gunships with no
    // ground force loses the map while it is flying home to rearm.
    const main = defsForFaction(faction).find((d) => d.role === ROLE.MainBattle)
    const light = defsForFaction(faction).find((d) => d.role === ROLE.AntiInfantry)
    const air = defsForFaction(faction).find((d) => d.role === ROLE.Air)
    // Tier three, once the lab exists. Ordered heavy-beam-artillery: the
    // brawler and the beam tank both want to be in the fight, and artillery is
    // only worth buying when there is already an army in front of it.
    const heavy = defsForFaction(faction).find((d) => d.role === ROLE.Heavy)
    const beam = defsForFaction(faction).find((d) => d.role === ROLE.Beam)
    const artillery = defsForFaction(faction).find((d) => d.role === ROLE.Artillery)

    const depth = Math.min(MAX_QUEUE_DEPTH - 2, Math.max(4, Math.floor(credits / 2500)))

    for (const def of [main, light, heavy, beam, artillery, air]) {
      if (!def) continue
      if (!this.build.canBuild(player, def.id)) continue
      if (this.build.queueDepth(player, def.tab) >= depth) continue
      if (credits < def.cost + RESERVE) continue
      // A squadron, not an air force. Each aircraft spends a third of its life
      // unarmed and in transit, so past a handful they stop adding pressure and
      // start replacing the ground army that was providing it.
      if (def.isAir && this.countRole(player, ROLE.Air) >= MAX_AIRCRAFT) continue
      // Artillery cannot defend itself: it has a hole in the middle of its
      // range where it simply does not shoot. Capped against the size of the
      // army that has to stand in front of it.
      if (def.role === ROLE.Artillery) {
        const escort = this.countRole(player, ROLE.MainBattle) + this.countRole(player, ROLE.Heavy)
        if (this.countRole(player, ROLE.Artillery) * ARTILLERY_ESCORT >= escort) continue
      }
      this.submit(player, { t: CMD.Queue, tab: def.tab, kind: def.id })
    }
  }

  /**
   * Gather, then commit.
   *
   * The obvious implementation — re-issue attack-move at the enemy base every
   * few seconds — dribbles every newly built unit across the map alone, where
   * it dies to the defending army one at a time. Two AIs doing that to each
   * other reach an attrition equilibrium and the match never resolves: the
   * bot-match sweep showed exactly that, with both sides holding full bases and
   * twenty-plus units at the tick cap.
   *
   * So the army rallies near its own base until it is worth committing, then
   * attacks as one wave, and falls back to regroup when the wave is spent.
   */
  private commandArmy(player: number, faction: number, tick: number): void {
    const healthy: number[] = []
    let value = 0
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s) || this.entities.owner[s] !== player) continue
      if (!this.entities.has(s, EF.Mobile)) continue
      if (this.entities.has(s, EF.Harvester)) continue
      const def = defById(this.entities.kind[s])
      if (!def || def.role === ROLE.Founder) continue
      // An aircraft flying home or sitting on a pad is not part of the army.
      // Counting it is how a player ends up committing on the strength of a
      // squadron that is parked: a probe of a stalled match found eight of nine
      // "army" units in the rearm cycle, and the AI attacking as though it had
      // nine.
      const state = this.entities.state[s]
      if (state === ST.Rearming || state === ST.RearmingInbound) continue
      value += def.cost
      // Withdrawing wounded units was tried and doubled the stall rate: an army
      // that keeps pulling a third of itself out never masses enough to commit,
      // so both sides bleed forever. Everything fights.
      healthy.push(this.entities.idAt(s))
    }
    const army = healthy
    if (army.length === 0) return
    void faction

    const rally = this.rallyPoint(player)

    // Defence outranks everything — but only when there is something to defend
    // *against*.
    //
    // A superweapon strike damages structures with nothing on the ground to
    // shoot back at, and the recall used to fire on the damage alone. Two of
    // the last stalls in the sweep were exactly that: eleven units parked at
    // home for the rest of the match, recalled every few seconds by an enemy
    // that was hitting the base from orbit and had no army at all.
    if (tick - this.baseUnderAttack[player] < 5 * TICK_HZ) {
      const centre = this.baseCentre(player)
      if (centre >= 0 && this.enemyNearBase(player, centre)) {
        this.attacking[player] = 0
        this.orderArmyTo(player, army, centre)
        return
      }
    }

    // Finishing is a different job from attacking, and the AI was only ever
    // taught the second one. A wave that trades evenly, falls back, rebuilds and
    // trades again is a match that never ends — a fifth of the sweep sat at the
    // tick cap with both sides alive and neither able to close. Once the enemy
    // is visibly broken, the threshold, the rally and the retreat all stop
    // applying: everything goes in and keeps going in.
    if (this.canFinish(player, value)) {
      this.attacking[player] = 1
      if (tick % (2 * TICK_HZ) === 0) {
        const target = this.pickAttackTarget(player)
        if (target >= 0) this.orderArmyTo(player, army, target)
      }
      return
    }

    if (this.attacking[player] === 1) {
      // The wave is spent — fall back and rebuild rather than feeding it.
      //
      // Except late, when falling back is how a match reaches the tick cap:
      // both sides regroup, rebuild to the same strength, and trade again. Past
      // the point where a rebuild can still change the outcome, the army stays
      // committed and the match resolves.
      const lateGame = this.ctx.time.tick > MATCH_TICK_CAP * 0.55
      if (!lateGame && value < this.thresholdFor(player) / 3) {
        this.attacking[player] = 0
        this.lastAttack[player] = tick
      } else if (tick % (5 * TICK_HZ) === 0) {
        this.commitWave(player, healthy)
      }
      return
    }

    // Gathering. Idle units walk to the rally point and wait there.
    if (rally >= 0 && tick % (3 * TICK_HZ) === 0) {
      const waiting = healthy.filter((id) => {
        const s = this.entities.resolve(id)
        return s >= 0 && this.entities.state[s] === ST.Idle && !this.nearCell(s, rally, 6)
      })
      if (waiting.length > 0) this.orderArmyTo(player, waiting, rally)
    }

    if (value < this.thresholdFor(player)) return
    if (tick - this.lastAttack[player] < 15 * TICK_HZ) return

    this.attacking[player] = 1
    this.lastAttack[player] = tick
    this.commitWave(player, healthy)
  }

  /** Any visible enemy unit within reach of the base. */
  private enemyNearBase(player: number, centre: number): boolean {
    const cx = centre % MAP_SIZE
    const cy = (centre / MAP_SIZE) | 0
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      const owner = this.entities.owner[s]
      if (owner < 0 || owner === player) continue
      if (this.entities.has(s, EF.Structure)) continue
      if (!this.vision.canSee(player, s)) continue
      const dx = (this.entities.x[s] >> 16) - cx
      const dy = (this.entities.y[s] >> 16) - cy
      if (dx * dx + dy * dy <= DEFEND_RADIUS * DEFEND_RADIUS) return true
    }
    return false
  }

  /**
   * Is the enemy broken enough to be finished off?
   *
   * Two ways in. Either they have almost nothing left standing — at which point
   * hesitating is how a won match becomes a stalled one — or this army is
   * comfortably larger than everything they have to answer it with, counting
   * their static defences, which is the case where a careful player commits and
   * this AI used to sit at its rally point.
   */
  private canFinish(player: number, armyValue: number): boolean {
    let structures = 0
    let enemyValue = 0
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      const owner = this.entities.owner[s]
      if (owner < 0 || owner === player) continue
      const def = defById(this.entities.kind[s])
      if (!def) continue
      if (this.entities.has(s, EF.Structure)) {
        structures++
        if (def.role === ROLE.Defense) enemyValue += def.cost
        continue
      }
      if (this.entities.has(s, EF.Harvester)) continue
      enemyValue += def.cost
    }
    if (structures === 0) return false
    // The floor is relative to what is left to beat, not absolute. A fixed one
    // stopped a five-unit army from walking over an enemy with two buildings
    // and nothing else — it was below the bar, the bar never moved, and the
    // match ran to the cap with the win sitting there uncollected.
    if (armyValue < Math.max(FINISH_FLOOR, enemyValue)) return false
    return structures <= 4 || armyValue > enemyValue * 2
  }

  /**
   * Send the army at one target, as one force.
   *
   * Splitting off a raiding party was tried twice and lost both times. The
   * theory was sound — make the defender answer two threats, and hit the
   * economy that replaces their losses — but the measurement was not: a 30%
   * detachment took the stall rate from 6 in 240 matches to 17, and removing it
   * again took 15 stalls in 80 down to 9. An army split in two arrives as two
   * forces that each lose, and the AI has no way to time them.
   *
   * Target selection still weighs economy above defences — that part was never
   * the problem. It is the *splitting* that failed.
   */
  private commitWave(player: number, army: number[]): void {
    if (army.length === 0) return
    const main = this.pickAttackTarget(player)
    if (main < 0) return
    this.orderArmyTo(player, army, main)
  }

  /** Enemy defensive strength within a few cells of a point. */
  private defenceNear(player: number, cell: number): number {
    const cx = cell % MAP_SIZE
    const cy = (cell / MAP_SIZE) | 0
    let strength = 0
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      const owner = this.entities.owner[s]
      if (owner < 0 || owner === player) continue
      const def = defById(this.entities.kind[s])
      if (!def || def.weapon === 0) continue
      const dx = (this.entities.x[s] >> 16) - cx
      const dy = (this.entities.y[s] >> 16) - cy
      if (dx * dx + dy * dy > 100) continue
      strength += def.isStructure ? 3 : 1
    }
    return strength
  }

  /**
   * This player's attack threshold, scaled by its temperament.
   *
   * A decaying threshold was tried and made things worse on both axes: armies
   * committed earlier and smaller, died piecemeal, and *more* matches ran to
   * the cap rather than fewer. Attrition is resolved by committing a big enough
   * force, not an earlier one. Tried a second time after the terrain rework, in
   * case bigger maps changed the answer: 13 stalls in 80 matches with a decaying
   * bar against 12 without. It does not.
   */
  private thresholdFor(player: number): number {
    const base = (this.personalityFor(player).attackThreshold * this.aggression[player]) / 100
    return base * this.handicap(player).attackScale
  }

  /** Live combat units this player owns. Harvesters and founders do not count. */
  private combatUnitCount(player: number): number {
    let n = 0
    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      if (this.entities.owner[slot] !== player) continue
      if (!this.entities.has(slot, EF.Mobile)) continue
      const def = defById(this.entities.kind[slot])
      if (!def || def.weapon === NO_WEAPON) continue
      n++
    }
    return n
  }

  private orderArmyTo(player: number, army: number[], cell: number): void {
    this.submit(player, {
      t: CMD.AttackMove,
      units: Uint32Array.from(army),
      x: this.world.cellCenterX(cell % MAP_SIZE),
      y: this.world.cellCenterY((cell / MAP_SIZE) | 0),
    })
  }

  /** A cell a few steps out from the base, on the side facing the enemy. */
  private rallyPoint(player: number): number {
    if (this.rally[player] >= 0) return this.rally[player]
    const centre = this.baseCentre(player)
    if (centre < 0) return -1
    const target = this.pickAttackTarget(player)
    if (target < 0) return centre

    const bx = centre % MAP_SIZE
    const by = (centre / MAP_SIZE) | 0
    const tx = target % MAP_SIZE
    const ty = (target / MAP_SIZE) | 0
    const angle = fix.atan2(fix.fromInt(ty - by), fix.fromInt(tx - bx))
    const rx = bx + ((fix.cos(angle) * 8) >> 16)
    const ry = by + ((fix.sin(angle) * 8) >> 16)
    if (!this.world.inBounds(rx, ry)) return centre
    this.rally[player] = ry * MAP_SIZE + rx
    return this.rally[player]
  }

  private nearCell(slot: number, cell: number, radius: number): boolean {
    const cx = cell % MAP_SIZE
    const cy = (cell / MAP_SIZE) | 0
    const ux = this.entities.x[slot] >> 16
    const uy = this.entities.y[slot] >> 16
    return Math.abs(ux - cx) <= radius && Math.abs(uy - cy) <= radius
  }

  /**
   * Where to attack. Known enemy structures first, then enemy start positions —
   * which is knowledge the AI is entitled to, because every player can read the
   * map's start markers.
   */
  private pickAttackTarget(player: number): number {
    // Priority order. Grinding down turrets while the enemy rebuilds behind
    // them is how an attrition stalemate starts; killing production is how it
    // ends.
    const priority: Record<string, number> = {
      [ROLE.Refinery]: 5,
      [ROLE.VehicleProduction]: 5,
      [ROLE.InfantryProduction]: 4,
      [ROLE.Power]: 4,
      [ROLE.Superweapon]: 6,
      [ROLE.BaseRoot]: 3,
      [ROLE.TechLab]: 2,
      [ROLE.AdvancedLab]: 2,
      [ROLE.Airbase]: 2,
      [ROLE.Defense]: 1,
    }
    let bestCell = -1
    let bestScore = -Infinity
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      const owner = this.entities.owner[s]
      if (owner < 0 || owner === player) continue
      if (!this.entities.has(s, EF.Structure)) continue
      if (!this.vision.canSee(player, s)) continue
      const def = defById(this.entities.kind[s])
      const cell = this.world.cellAt(fix.raw(this.entities.x[s]), fix.raw(this.entities.y[s]))
      if (cell < 0) continue
      // Discounted by how heavily defended it is. Without this the AI re-attacks
      // the same wall indefinitely: it keeps picking the highest-value target
      // regardless of whether it has ever been able to reach it.
      const score = (priority[def?.role ?? ''] ?? 1) / (1 + this.defenceNear(player, cell) * 0.4)
      if (score <= bestScore) continue
      bestScore = score
      bestCell = cell
    }
    if (bestCell >= 0) return bestCell

    // Nothing visible. Where one *was* seen beats where one started: a rebuilt
    // base is somewhere else, and an army parked on an empty start position
    // while the enemy expands elsewhere is a match that runs to the tick cap.
    const remembered = this.rememberedEnemyStructure(player)
    if (remembered >= 0) return remembered

    return this.searchCell(player)
  }

  /**
   * Somewhere to look when the enemy cannot be found at all.
   *
   * Cycles: each call advances one step, so an army that arrives and finds
   * nothing is sent somewhere else next time rather than standing on the same
   * empty ground for the rest of the match. Deterministic — it is a counter,
   * not a random walk.
   */
  private searchCell(player: number): number {
    const candidates: number[] = []
    for (let p = 0; p < MAX_PLAYERS; p++) {
      if (p === player) continue
      const sx = this.world.starts[p * 2]
      const sy = this.world.starts[p * 2 + 1]
      if (sx >= 0) candidates.push(sy * MAP_SIZE + sx)
    }
    // Quadrant centres, so a base built well away from any start is still swept.
    const q = MAP_SIZE >> 2
    for (const [x, y] of [
      [q, q],
      [MAP_SIZE - q, q],
      [q, MAP_SIZE - q],
      [MAP_SIZE - q, MAP_SIZE - q],
      [MAP_SIZE >> 1, MAP_SIZE >> 1],
    ]) {
      candidates.push(y * MAP_SIZE + x)
    }
    if (candidates.length === 0) return -1
    const cell = candidates[this.searchStep[player] % candidates.length]
    this.searchStep[player]++
    return cell
  }

  // --- helpers -------------------------------------------------------------

  private countRole(player: number, role: string): number {
    let n = 0
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s) || this.entities.owner[s] !== player) continue
      if (this.entities.has(s, EF.Building)) continue
      if (defById(this.entities.kind[s])?.role === role) n++
    }
    return n
  }

  private countHarvesters(player: number): number {
    let n = 0
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (this.entities.isAlive(s) && this.entities.owner[s] === player) {
        if (this.entities.has(s, EF.Harvester)) n++
      }
    }
    return n
  }

  private submit(player: number, cmd: Partial<Command> & { t: number }): boolean {
    if (!this.relay) return false
    const rejection = this.relay.submit({ ...cmd, slot: player } as never)
    return rejection === null
  }

  /** Idle-harvester count, used by the bot-match record. */
  idleHarvesters(player: number): number {
    let n = 0
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s) || this.entities.owner[s] !== player) continue
      if (!this.entities.has(s, EF.Harvester)) continue
      if (this.entities.state[s] === ST.Idle) n++
    }
    return n
  }

  /** Exposed so a test can assert the AI never commands what it cannot see. */
  visibleEnemySlots(player: number): number[] {
    const out: number[] = []
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      const owner = this.entities.owner[s]
      if (owner < 0 || owner === player) continue
      if (this.vision.canSee(player, s)) out.push(s)
    }
    return out
  }
}

export { NO_ENTITY }
